import { yupResolver } from "@hookform/resolvers/yup";
import { Buttonv2 } from "@itandi/itandi-bb-ui/Buttonv2";
import { FormControl } from "@itandi/itandi-bb-ui/FormControl";
import { FormControlText } from "@itandi/itandi-bb-ui/FormControlText";
import { FormGroup } from "@itandi/itandi-bb-ui/FormGroup";
import { Input } from "@itandi/itandi-bb-ui/Input";
import { ModalBody } from "@itandi/itandi-bb-ui/ModalBody";
import { ModalFooter } from "@itandi/itandi-bb-ui/ModalFooter";
import { ModalHeader } from "@itandi/itandi-bb-ui/ModalHeader";
import { Modalv2 } from "@itandi/itandi-bb-ui/Modalv2";
import { useNotification } from "@itandi/itandi-bb-ui/NotificationBarStore";
import { useId } from "react";
import { useForm } from "react-hook-form";
import useSWRMutation from "swr/mutation";
import { useIsItandi } from "~/context/VendorSession";
import type { VendorUser, VendorUserSettingParam } from "~/models/VendorUser";
import { createVendorUserValidationScheme } from "~/models/VendorUser";
import { isSuccess } from "~/utils/api/api_handler";
import { create, update } from "~/utils/api/vendors/VendorUser";
import { generatePath } from "~/utils/generatePath";

type Props = {
	onClose: () => void;
	vendorUser?: VendorUser | null;
};

export function VendorUserModal({ vendorUser = null, onClose }: Props) {
	const { notify } = useNotification();
	const formId = useId();
	const canEditSlackMemberId = useIsItandi();

	const {
		register,
		handleSubmit,
		formState: { errors }
	} = useForm<VendorUserSettingParam>({
		defaultValues: {
			email: vendorUser?.email ?? "",
			name: vendorUser?.name ?? "",
			slackMemberId: vendorUser?.slackMemberId ?? "",
			tel: vendorUser?.tel ?? ""
		},
		mode: "onSubmit",
		resolver: yupResolver(
			createVendorUserValidationScheme(canEditSlackMemberId)
		)
	});

	const { trigger, isMutating } = useSWRMutation(
		generatePath("/vendors/vendor_users"),
		async (_key: string, { arg }: { arg: VendorUserSettingParam }) =>
			vendorUser === null ? create(arg) : update(arg, vendorUser.id)
	);

	const onSubmit = handleSubmit(async (submitValue) => {
		const payload: VendorUserSettingParam = {
			email: submitValue.email,
			name: submitValue.name,
			tel: submitValue.tel,
			...(canEditSlackMemberId
				? {
						slackMemberId:
							submitValue.slackMemberId === ""
								? null
								: submitValue.slackMemberId
					}
				: {})
		};
		const result = await trigger(payload);
		if (isSuccess(result)) {
			onClose();
			notify({ content: "保存しました。", variant: "success" });
		} else {
			notify({
				content: result.message ?? "エラーが発生しました。",
				variant: "error"
			});
		}
	});

	return (
		<Modalv2 isOpen onClose={onClose} size="s">
			<ModalHeader title={vendorUser === null ? "担当者追加" : "担当者編集"} />
			<ModalBody>
				<form id={formId} noValidate onSubmit={onSubmit}>
					<FormGroup variant="vertical">
						<FormControl chip="必須" isInvalid={!!errors.name} label="担当者名">
							<Input {...register("name")} />
							<FormControlText error={errors.name?.message} />
						</FormControl>
						<FormControl chip="必須" isInvalid={!!errors.tel} label="電話番号">
							<Input {...register("tel")} />
							<FormControlText error={errors.tel?.message} />
						</FormControl>
						<FormControl
							chip="必須"
							isInvalid={!!errors.email}
							label="メールアドレス"
						>
							<Input {...register("email")} />
							<FormControlText error={errors.email?.message} />
						</FormControl>
						{canEditSlackMemberId ? (
							<FormControl isInvalid={!!errors.slackMemberId} label="Slack ID">
								<Input
									placeholder="例）U0123456789"
									{...register("slackMemberId")}
								/>
								<FormControlText error={errors.slackMemberId?.message} />
							</FormControl>
						) : null}
					</FormGroup>
				</form>
			</ModalBody>
			<ModalFooter
				leftArea={
					<Buttonv2 disabled={isMutating} onClick={onClose} variant="secondary">
						キャンセル
					</Buttonv2>
				}
				rightArea={
					<Buttonv2
						form={formId}
						loading={isMutating}
						type="submit"
						variant="primary"
					>
						保存
					</Buttonv2>
				}
			/>
		</Modalv2>
	);
}
